import {useContext, useMemo} from "react";
import {FormattedMessage} from "react-intl";
import Tag from "./tag";
import TireRegions from "./tire-regions";
import tireImage from "../assets/Images/tire.svg";
import {CONFIG, SeasonEnum, TIRE_STATE} from "../constants";
import {Tire as TireModel} from "../models/tire";
import {tireDescription} from "../utils/tires";
import {AppStoreContext} from "../store/app-store.ts";
import {TireConfig} from "../models/report.ts";
import {TireImagePosition} from "../models/enums/tire-image-position.enum.ts";
import {extractProductionDateWeekYear, productionMonthYear, tireAgeInYears} from "@arhs-fleetback/common-utils/tires";

const POSITION_STYLES: Record<TireImagePosition, string> = {
  [TireImagePosition.RIGHT]: "right-0 -mr-14",
  [TireImagePosition.LEFT]: "left-0 -ml-14",
};

type TireProps = {
  tire: Partial<TireModel>;
  config?: TireConfig;
  position?: TireImagePosition;
  refDate?: Date | null;
}

const Tire = ({ tire, config, position = TireImagePosition.RIGHT, refDate }: TireProps) => {
  const { locale } = useContext(AppStoreContext);
  const { translation, color } = (tire.recommendation && CONFIG[tire.recommendation]) || CONFIG[TIRE_STATE.ERROR];
  const description = tireDescription(tire);
  const { tireBrand, season, pressure, depthRegions, dot } = tire;
  const hasSpecifiedSeason = season && season !== SeasonEnum.UNSPECIFIED;
  const hasTireDetails = [tireBrand, pressure, hasSpecifiedSeason, description].some(Boolean);

  const weekYear = useMemo(
    () => (refDate ? extractProductionDateWeekYear(dot, refDate) : null),
    [dot, refDate],
  );
  const monthYear = useMemo(() => productionMonthYear(weekYear), [weekYear]);
  const ageInYears = useMemo(() => tireAgeInYears(weekYear, refDate), [weekYear, refDate]);

  const productionDateYearMonth = monthYear
    ? `${monthYear.year}/${String(monthYear.month).padStart(2, '0')}`
    : null;
  const tireLabel = tire.tireLabel?.[locale];
  return (
    <div className="flex flex-col text-dark flex-1 items-center w-full bg-lightGrey rounded-3xl p-12 relative">
      {tireLabel && translation && <Tag content={<FormattedMessage {...translation} />} color={color} />}
      <div className="flex-1 flex flex-col items-center justify-center gap-4">
        {tire.depth != null && <p className="text-2vw">{tire.depth.toFixed(1)}</p>}
        {depthRegions && config && <TireRegions config={config} season={season} depthRegion={depthRegions} />}
        {hasTireDetails && (
          <ul className="text-1vw text-grey70 font-normal flex flex-col gap-1">
            {tireBrand?.name && <li>{tireBrand.name}</li>}
            {description && <li>{description}</li>}
            {hasSpecifiedSeason && <li><FormattedMessage id={`tire.tireType.${season}`} defaultMessage={season} /></li>}
            {pressure && <li><FormattedMessage id="tire.pressure" defaultMessage="Pressure: {pressure}" values={{ pressure }} /></li>}
            {productionDateYearMonth
              ? <li><FormattedMessage id="tire.productionDate" defaultMessage="{productionDateYearMonth} ({tireAgeInYearsRoundedDown, plural, one {# year} other {# years}})" values={{ productionDateYearMonth, tireAgeInYearsRoundedDown: ageInYears ?? 0 }} /></li>
              : dot && <li><FormattedMessage id="tire.DOT" defaultMessage="Dot: {dot}" values={{ dot }} /></li>
            }
          </ul>
        )}
      </div>
      <img src={tireImage} className={`w-28 absolute ${POSITION_STYLES[position]}`} alt="Tire" />
    </div>
  );
};

export default Tire;
